import { AppProvider } from '@shopify/polaris';
import { Provider as AppBridgeProvider } from '@shopify/app-bridge-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@shopify/polaris/build/esm/styles.css';

// App Bridge será carregado da CDN da Shopify quando embedado
// Isso é verificado automaticamente pela Shopify durante a revisão

import './i18n/config';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Reviews from './pages/Reviews';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import BillingConfirm from './pages/BillingConfirm';
import ThemeSetup from './pages/ThemeSetup';
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminShops from './pages/admin/Shops';
import AdminPlans from './pages/admin/Plans';
import AdminSupportTickets from './pages/admin/SupportTickets';
import AdminRoute from './components/AdminRoute';
import { isLocalDevelopment } from './utils/env';

// Obter configuração do App Bridge da URL
// Para apps incorporados (embedded = true), o App Bridge deve ser usado SEMPRE quando embedado
// O Shopify sempre fornece o parâmetro 'host' quando o app é acessado através do Shopify Admin
function getAppBridgeConfig() {
  const isLocal = isLocalDevelopment();
  
  // Em desenvolvimento local, não usar App Bridge
  if (isLocal) {
    return null;
  }

  // Em produção, verificar se o app está embedado (tem parâmetro host)
  // Para apps incorporados, o host sempre deve estar presente quando embedado
  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host');
  const shopFromUrl = urlParams.get('shop');
  
  // Obter shop domain de múltiplas fontes (URL, localStorage)
  const getShopDomain = (): string | null => {
    // 1. Tentar obter da URL primeiro (mais confiável)
    if (shopFromUrl) {
      return shopFromUrl;
    }
    
    // 2. Tentar obter do localStorage
    const savedShopDomain = localStorage.getItem('shop_domain');
    if (savedShopDomain) {
      return savedShopDomain;
    }
    
    return null;
  };
  
  const shop = getShopDomain();
  
  // Obter API key da meta tag ou variável de ambiente
  const apiKey = (() => {
    // Tentar obter da meta tag primeiro (mais confiável)
    const metaTag = document.querySelector('meta[name="shopify-api-key"]');
    if (metaTag && metaTag.getAttribute('content')) {
      const content = metaTag.getAttribute('content');
      if (content && content.trim() !== '') {
        return content;
      }
    }
    // Fallback para variável de ambiente
    return import.meta.env.VITE_SHOPIFY_API_KEY || '';
  })();

  // Se não temos API key, não podemos inicializar o App Bridge
  if (!apiKey || apiKey.trim() === '') {
    return null;
  }

  // Para apps incorporados, o host sempre deve estar presente quando embedado
  // Se temos host, inicializar o App Bridge
  if (host) {
    // App Bridge requer o campo 'shop' - usar shop da URL ou localStorage
    // Se não temos shop, tentar extrair do host ou usar um valor padrão
    const shopDomain = shop || (host ? extractShopFromHost(host) : null);
    
    if (!shopDomain) {
      console.warn('App Bridge: shop domain not found, but host is present. App Bridge may not work correctly.');
    }
    
    return {
      apiKey,
      host: decodeURIComponent(host),
      ...(shopDomain ? { shop: shopDomain } : {}),
      forceRedirect: true,
    };
  }

  // Se não temos host mas temos shop, pode ser que o app ainda não foi totalmente embedado
  // Para apps incorporados, o host sempre deve estar presente quando acessado via Shopify Admin
  // Se não está presente, pode ser um problema de configuração ou o app não está sendo acessado corretamente
  // Neste caso, não inicializamos o App Bridge (ele requer o host para funcionar)
  // O app funcionará sem App Bridge até que o host esteja disponível
  return null;
}

// Helper para extrair shop domain do host (se possível)
// Nota: O host geralmente vem no formato: base64(shop.myshopify.com/admin)
// Mas não podemos decodificar facilmente, então retornamos null
// O shop deve vir do parâmetro 'shop' na URL ou localStorage
function extractShopFromHost(_host: string): string | null {
  return null;
}

// App Bridge é carregado automaticamente pela Shopify quando o app está embedado
// Para apps incorporados (embedded = true), o App Bridge deve ser usado SEMPRE em produção
// A Shopify verifica isso automaticamente durante a revisão

function App() {
  const appBridgeConfig = getAppBridgeConfig();
  const isLocal = isLocalDevelopment();

  // Em desenvolvimento local, renderizar sem App Bridge
  if (isLocal || !appBridgeConfig) {
    // Em desenvolvimento local, remover rotas protegidas
    if (isLocal) {
      return (
        <AppProvider i18n={{} as any}>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Layout>
                      <Dashboard />
                    </Layout>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <Layout>
                      <Products />
                    </Layout>
                  }
                />
                <Route
                  path="/reviews"
                  element={
                    <Layout>
                      <Reviews />
                    </Layout>
                  }
                />
              <Route
                path="/settings"
                element={
                  <Layout>
                    <Settings />
                  </Layout>
                }
              />
              <Route
                path="/theme-setup"
                element={
                  <Layout>
                    <ThemeSetup />
                  </Layout>
                }
              />
              <Route
                path="/billing"
                  element={
                    <Layout>
                      <Billing />
                    </Layout>
                  }
                />
                <Route
                  path="/billing/confirm"
                  element={
                    <Layout>
                      <BillingConfirm />
                    </Layout>
                  }
                />
                <Route path="/login" element={<Login />} />
                {/* Admin routes - NÃO aparecem no Shopify */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/shops"
                  element={
                    <AdminRoute>
                      <AdminShops />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/plans"
                  element={
                    <AdminRoute>
                      <AdminPlans />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/support"
                  element={
                    <AdminRoute>
                      <AdminSupportTickets />
                    </AdminRoute>
                  }
                />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </AppProvider>
      );
    }

    // Se não é local mas também não tem App Bridge, usar rotas protegidas
    return (
      <AppProvider i18n={{} as any}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Products />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reviews"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Reviews />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/theme-setup"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ThemeSetup />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Billing />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/confirm"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BillingConfirm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Admin routes - Acessíveis diretamente em produção */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/shops"
                element={
                  <AdminRoute>
                    <AdminShops />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/plans"
                element={
                  <AdminRoute>
                    <AdminPlans />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <AdminRoute>
                    <AdminSupportTickets />
                  </AdminRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </AppProvider>
    );
  }

  // Se houver config (embedado no Shopify), usar App Bridge
  return (
    <AppBridgeProvider config={appBridgeConfig}>
      <AppProvider i18n={{} as any}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Products />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reviews"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Reviews />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/theme-setup"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ThemeSetup />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Billing />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing/confirm"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BillingConfirm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/login" element={<Login />} />
              {/* Admin routes - NÃO aparecem no Shopify */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/shops"
                element={
                  <AdminRoute>
                    <AdminShops />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/plans"
                element={
                  <AdminRoute>
                    <AdminPlans />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <AdminRoute>
                    <AdminSupportTickets />
                  </AdminRoute>
                }
              />
              {/* Catch-all route for 404 */}
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </AppProvider>
    </AppBridgeProvider>
  );
}

export default App;




